import supabase from './supabase';

export type ContactsTablePreferences = {
  columnOrder: string[];
  visibleColumns: Record<string, boolean>;
  filters: {
    selectedSource: string;
    selectedTag?: string; // Legacy support
    selectedTags?: string[]; // New multi-select
    selectedStage: string;
    selectedProspectStatus: string;
    selectedLeadStatus: string;
    selectedCity?: string; // Legacy support
    selectedCities?: string[]; // New multi-select
  };
  sorting: {
    sortKey: 'recent' | 'name' | 'route_index_asc' | 'route_index_desc';
    columnSorting?: Array<{ id: string; desc: boolean }>;
  };
  pagination: {
    currentPage: number;
    itemsPerPage: number;
  };
  columnFilters?: Array<{ id: string; value: any }>;
};

/**
 * Save user preferences to database
 */
export async function saveUserPreferences(
  userId: string,
  orgId: string,
  pageName: string,
  preferences: any
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('user_preferences')
      .upsert(
        {
          user_id: userId,
          org_id: orgId,
          page_name: pageName,
          preferences: preferences,
          updated_at: new Date().toISOString(),
        },
        {
          onConflict: 'user_id,org_id,page_name',
        }
      );

    if (error) {
      console.error('[UserPreferences] Save error:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error: any) {
    console.error('[UserPreferences] Save exception:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Load user preferences from database
 */
export async function loadUserPreferences(
  userId: string,
  orgId: string,
  pageName: string
): Promise<{ success: boolean; preferences?: any; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('user_preferences')
      .select('preferences')
      .eq('user_id', userId)
      .eq('org_id', orgId)
      .eq('page_name', pageName)
      .maybeSingle();

    if (error) {
      console.error('[UserPreferences] Load error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, preferences: data?.preferences || null };
  } catch (error: any) {
    console.error('[UserPreferences] Load exception:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Migrate localStorage preferences to database
 */
export async function migrateLocalStorageToDatabase(
  userId: string,
  orgId: string,
  pageName: string = 'contacts'
): Promise<{ success: boolean; error?: string; migrated?: boolean }> {
  try {
    // Check if preferences already exist in database
    const existing = await loadUserPreferences(userId, orgId, pageName);
    if (existing.success && existing.preferences) {
      return { success: true, migrated: false, error: 'Preferences already exist in database' };
    }

    // Load from localStorage
    const columnOrder = localStorage.getItem('crm_contacts_column_order');
    const visibleColumns = localStorage.getItem('crm_contacts_visible_columns');

    if (columnOrder || visibleColumns) {
      const prefs: ContactsTablePreferences = {
        columnOrder: columnOrder ? JSON.parse(columnOrder) : [],
        visibleColumns: visibleColumns ? JSON.parse(visibleColumns) : {},
        filters: {
          selectedSource: '',
          selectedTag: '',
          selectedStage: '',
          selectedProspectStatus: '',
          selectedLeadStatus: '',
          selectedCity: '',
        },
        sorting: {
          sortKey: 'recent',
        },
        pagination: {
          currentPage: 1,
          itemsPerPage: 10,
        },
      };

      const result = await saveUserPreferences(userId, orgId, pageName, prefs);

      if (result.success) {
        // Clear localStorage after successful migration
        localStorage.removeItem('crm_contacts_column_order');
        localStorage.removeItem('crm_contacts_visible_columns');
        return { success: true, migrated: true };
      }

      return result;
    }

    return { success: true, migrated: false, error: 'No localStorage data to migrate' };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
}

